/* eslint-disable react-refresh/only-export-components --
   Widget module: the FormWidget descriptor (sqlExpressionWidget) and its renderer
   component are one cohesive plugin registration — the renderer is reached only
   through the descriptor. Splitting the renderer into a sibling file for a
   dev-HMR benefit fragments the widget with no reuse gain. */
import { useEffect, useRef, useState } from "react";
import Editor, { Monaco } from "@monaco-editor/react";
import type { editor } from "monaco-editor";
import { adminRpc } from "../../../services/admin-rpc-client";
import { sqlFirewallQueryFull } from "../../qubes-table/table-model";
import {
  SchemaInfo,
  registerSqlCompletionProvider,
} from "../../../pages/sql-query-three/sqlCompletion";
import type { TableOrView } from "../../../pages/sql-query-three/sqlCompletionCore";
import { FormWidgetService, FormWidgetProps } from "./form-widget-service";
import { errorMessage } from "../../../utils/error-message";

// ── sql-expression: the calculated-field / row-filter / validation editor ──
// FEATURE-20260716-sql-firewall-policy-editor
//
// A registered form widget ({type:"widget", widget:"sql-expression",
// config:{mode}}). Reads its TABLE from the form's ambient FormContext
// (single-select lineage — the page guarantees exactly one), builds a
// single-table completion catalog from the firewall's own metadata (columns
// via a `SELECT * LIMIT 0` probe THROUGH the firewall — so completion matches
// exactly what the firewall accepts, calculated fields included — plus the
// table's declared calculated fields and auto-joins), and live-validates on
// defocus by probing the expression through the firewall, showing the
// transformed SQL (macros expanded, tables resolved) on success and the
// engine's error inline on failure.

export type SqlExpressionMode =
  | "select-expression" // table_fields.select_expression (calculated)
  | "generated-expression" // table_fields.expression (generated; not probe-able)
  | "row-filter" // table_access.row_filter (boolean over the table)
  | "join" // table_joins.join_expression (left = from-table, right = to-table)
  | "validation-query"; // table_validation.query (standalone SELECT w/ @params)

type ValidationState =
  | { kind: "idle" }
  | { kind: "checking" }
  | { kind: "valid"; transformedSql?: string }
  | { kind: "invalid"; message: string }
  | { kind: "unavailable"; message: string }; // mode not probe-able

// ── Catalog ─────────────────────────────────────────────────────────────────

const catalogCache = new Map<string, Promise<SchemaInfo>>();

async function buildCatalog(tableUid: string, tableName: string): Promise<SchemaInfo> {
  // Columns: a LIMIT 0 probe through the firewall returns the column list the
  // firewall itself serves for this table — physical + calculated, resolved.
  const probe = adminRpc.queryFull(`SELECT * FROM ${tableName} LIMIT 0`);
  // Declared calculated fields + auto-joins from the firewall model tables.
  // These are ENRICHMENT (extra labels), not the load-bearing column list — and
  // the firewall can deny access to table_fields / table_joins independently of
  // the base table. A denial there must degrade to "no extra labels", NOT kill
  // column completion, so each falls back to empty rows on rejection (logged,
  // not swallowed) rather than failing the whole Promise.all (which blanked
  // completion when table_joins was access-denied on dev —
  // BUG-20260720-firewall-field-completion-dead-on-table-joins-denied).
  const esc = tableUid.replace(/'/g, "''");
  const empty = { rows: [] as Record<string, unknown>[], rowCount: 0 };
  // Degrade to no-labels on failure, but LOG it — a denial here is expected
  // (a missing firewall grant) yet still worth surfacing, and this is also where
  // an UNexpected failure (e.g. the base table's own metadata denied) would
  // otherwise vanish. Swallowing silently hid exactly that diagnostic.
  const enrich = (label: string, p: Promise<{ rows: Record<string, unknown>[]; rowCount: number }>) =>
    p.catch((e) => {
      console.warn(
        `sql-expression completion: ${label} lookup failed for ${tableName}; ` +
          `continuing without those labels.`,
        e,
      );
      return empty;
    });
  const fields = enrich(
    "table_fields",
    adminRpc.query(`SELECT column_name, field_kind FROM table_fields WHERE table_uid = '${esc}'`),
  );
  const joins = enrich(
    "table_joins",
    adminRpc.query(
      `SELECT join_name, join_expression, join_type, toTable.table_name AS "toTable.table_name"
     FROM table_joins WHERE from_table_uid = '${esc}'`,
    ),
  );

  const [probeR, fieldsR, joinsR] = await Promise.all([probe, fields, joins]);
  const columns = (probeR.columns ?? []).map((c) => c.name);
  const calculated = fieldsR.rows
    .filter((r) => r["field_kind"] === "calculated")
    .map((r) => String(r["column_name"]));
  const autoJoins = joinsR.rows.map((r) => ({
    joinName: String(r["join_name"]),
    toTable: String(r["toTable.table_name"] ?? ""),
    joinExpression: String(r["join_expression"] ?? ""),
    joinType: String(r["join_type"] ?? "LEFT"),
  }));

  // Joined tables also become relations so dot-completion (join.column) works.
  const joinedNames = Array.from(new Set(autoJoins.map((j) => j.toTable).filter(Boolean)));
  const joinedRelations = await Promise.all(
    joinedNames.map(async (name) => {
      try {
        const jp = await adminRpc.queryFull(`SELECT * FROM ${name} LIMIT 0`);
        return {
          schema: "",
          name,
          kind: "table" as const,
          columns: (jp.columns ?? []).map((c) => c.name),
        };
      } catch {
        return { schema: "", name, kind: "table" as const, columns: [] };
      }
    }),
  );

  return {
    relations: [
      {
        schema: "",
        name: tableName,
        kind: "table",
        columns,
        calculatedFields: calculated,
        autoJoins,
      },
      ...joinedRelations,
    ],
    functions: [],
    foreignKeys: [],
  };
}

function catalogFor(tableUid: string, tableName: string): Promise<SchemaInfo> {
  const key = `${tableUid}:${tableName}`;
  let p = catalogCache.get(key);
  if (!p) {
    p = buildCatalog(tableUid, tableName);
    catalogCache.set(key, p);
    // A failed build shouldn't poison the cache.
    p.catch(() => catalogCache.delete(key));
  }
  return p;
}

// Whole-schema catalog for validation-query mode: a validation query is a full
// SELECT that can reference ANY table, so completion needs every table + its
// columns (like the standalone SQL editor), not just the context table. One
// information_schema sweep, no-transform (admin-only, which the firewall editor
// already is). Cached process-wide.
let fullSchemaPromise: Promise<SchemaInfo> | null = null;

function fullSchemaCatalog(): Promise<SchemaInfo> {
  if (!fullSchemaPromise) {
    fullSchemaPromise = (async () => {
      const rows = (
        await sqlFirewallQueryFull(
          `SELECT table_schema, table_name, column_name
           FROM information_schema.columns
           WHERE table_schema NOT IN ('pg_catalog', 'information_schema', 'pg_toast')
           ORDER BY table_schema, table_name, ordinal_position`,
          { noTransform: true },
        )
      ).rows;
      const relMap = new Map<string, TableOrView>();
      for (const r of rows) {
        const schema = String(r["table_schema"]);
        const name = String(r["table_name"]);
        const key = `${schema}.${name}`;
        let rel = relMap.get(key);
        if (!rel) {
          rel = { schema, name, kind: "table", columns: [] };
          relMap.set(key, rel);
        }
        rel.columns.push(String(r["column_name"]));
      }
      return { relations: [...relMap.values()], functions: [], foreignKeys: [] };
    })();
    fullSchemaPromise.catch(() => (fullSchemaPromise = null));
  }
  return fullSchemaPromise;
}

// Resolve a table_metadata uid to its (name, columns) — the right-hand side of
// a join, chosen in the form by uid. Cached by uid.
const tableByUidCache = new Map<string, Promise<{ name: string; columns: string[] } | null>>();

function tableByUid(uid: string): Promise<{ name: string; columns: string[] } | null> {
  let p = tableByUidCache.get(uid);
  if (!p) {
    p = (async () => {
      const esc = uid.replace(/'/g, "''");
      const meta = await adminRpc.query(
        `SELECT table_name FROM table_metadata WHERE uid = '${esc}'`,
      );
      const name = meta.rows[0]?.["table_name"];
      if (!name) return null;
      const probe = await adminRpc.queryFull(`SELECT * FROM ${name} LIMIT 0`);
      return { name: String(name), columns: (probe.columns ?? []).map((c) => c.name) };
    })();
    tableByUidCache.set(uid, p);
    p.catch(() => tableByUidCache.delete(uid));
  }
  return p;
}

// ── Validation probes ───────────────────────────────────────────────────────

/**
 * Build the LIMIT-0 probe statement for a mode, or null when not probe-able.
 * For join mode, toTableName supplies the right-hand relation.
 */
function probeSql(
  mode: SqlExpressionMode,
  expr: string,
  tableName?: string,
  toTableName?: string,
): string | null {
  const e = expr.trim();
  if (!e) return null;
  switch (mode) {
    case "select-expression":
      return tableName ? `SELECT ${e} AS __probe FROM ${tableName} LIMIT 0` : null;
    case "row-filter":
      return tableName ? `SELECT 1 AS __probe FROM ${tableName} WHERE ${e} LIMIT 0` : null;
    case "join":
      // left = from-table, right = to-table. Probe by aliasing them so the
      // firewall parser resolves left./right. exactly as it will at query time.
      return tableName && toTableName
        ? `SELECT 1 AS __probe FROM ${tableName} left JOIN ${toTableName} right ON ${e} LIMIT 0`
        : null;
    case "validation-query": {
      // @column placeholders bind at write time; substitute NULLs to probe shape.
      const probed = e.replace(/@[A-Za-z_][A-Za-z0-9_]*/g, "NULL");
      return probed;
    }
    case "generated-expression":
      // queryvar./gouid. expressions are engine-side write macros — no SQL probe.
      return null;
  }
}

// ── Widget ──────────────────────────────────────────────────────────────────

// Monaco completion providers are GLOBAL per language — one registration
// serves every "sql" editor on the page. Register once (on first widget
// mount) against module-level refs; each editor updates the refs when it
// gains focus, so completion always reflects the focused editor's table.
const activeSchemaRef: { current: SchemaInfo | null } = { current: null };
const activeTablesRef: { current: string[] } = { current: [] };
const activeExtraAliasesRef: { current: Record<string, string> } = { current: {} };
const activeSuppressBareRef: { current: boolean } = { current: false };
const activeAtFieldColumnsRef: { current: string[] } = { current: [] };
let providerRegistered = false;

function ensureProvider(monaco: Monaco) {
  if (providerRegistered) return;
  providerRegistered = true;
  // Deliberately never disposed: the refs null out when no editor is focused,
  // and a leaked no-op provider is cheaper than juggling refcounts across
  // mounts. (SqlQueryPage registers its own provider on a different route.)
  registerSqlCompletionProvider(monaco, activeSchemaRef, {
    implicitTablesRef: activeTablesRef,
    extraAliasesRef: activeExtraAliasesRef,
    suppressBareColumnsRef: activeSuppressBareRef,
    atFieldColumnsRef: activeAtFieldColumnsRef,
  });
}

function SqlExpressionControl(props: FormWidgetProps) {
  const mode = (props.formItem.config?.["mode"] as SqlExpressionMode) ?? "select-expression";
  const table = props.context?.table;
  const value = typeof props.formData[props.formItem.name] === "string"
    ? (props.formData[props.formItem.name] as string)
    : "";

  const [validation, setValidation] = useState<ValidationState>({ kind: "idle" });
  const [schemaInfo, setSchemaInfo] = useState<SchemaInfo | null>(null);
  const schemaInfoRef = useRef<SchemaInfo | null>(null);
  schemaInfoRef.current = schemaInfo;

  // Validation-query mode: the @field input columns are the validation's own
  // table's columns (the row being inserted/updated). Loaded from the single-
  // table catalog even though the editor completes against the whole schema.
  const atFieldColumnsRef = useRef<string[]>([]);

  const focusedRef = useRef(false);

  // Join mode: the RIGHT side is the to-table, chosen in the form's
  // to_table_uid select — a sibling field. Track it so completion updates
  // when the target changes.
  const toTableUid =
    mode === "join" ? (props.formData["to_table_uid"] as string | undefined) : undefined;

  useEffect(() => {
    if (!table) return;
    let live = true;

    const build = async (): Promise<SchemaInfo> => {
      // A validation query is a full statement — complete against the WHOLE
      // schema (any table), not just the context table. Separately load the
      // context table's columns for @field completion.
      if (mode === "validation-query") {
        catalogFor(table.uid, table.tableName)
          .then((c) => {
            atFieldColumnsRef.current = c.relations[0]?.columns ?? [];
            if (focusedRef.current) activeAtFieldColumnsRef.current = atFieldColumnsRef.current;
          })
          .catch(() => {});
        return fullSchemaCatalog();
      }

      const fromCatalog = await catalogFor(table.uid, table.tableName);
      if (mode !== "join") return fromCatalog;
      // Merge the to-table as a second relation so right.<col> resolves.
      if (!toTableUid) return fromCatalog;
      const to = await tableByUid(toTableUid);
      if (!to) return fromCatalog;
      return {
        ...fromCatalog,
        relations: [
          ...fromCatalog.relations,
          { schema: "", name: to.name, kind: "table" as const, columns: to.columns },
        ],
      };
    };

    build().then((s) => {
      if (!live) return;
      setSchemaInfo(s);
      // If the catalog resolves while this editor already has focus, the
      // focus handler's snapshot was null — refresh the shared provider ref.
      if (focusedRef.current) {
        activeSchemaRef.current = s;
        applyAliases();
      }
    });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table?.uid, mode, toTableUid]);

  const validate = async (expr: string) => {
    // For join mode the right-hand relation is the to-table (2nd merged rel).
    const toTableName =
      mode === "join" ? schemaInfoRef.current?.relations[1]?.name : undefined;
    const sql = probeSql(mode, expr, table?.tableName, toTableName);
    if (!expr.trim()) {
      setValidation({ kind: "idle" });
      return;
    }
    if (!sql) {
      setValidation({
        kind: "unavailable",
        message:
          mode === "generated-expression"
            ? "Generated expressions (queryvar./gouid./literals) are validated by the engine at write time."
            : "Select a table to enable validation.",
      });
      return;
    }
    setValidation({ kind: "checking" });
    try {
      const resp = await adminRpc.queryFull(sql);
      setValidation({ kind: "valid", transformedSql: resp.transformedSql });
    } catch (e) {
      setValidation({ kind: "invalid", message: errorMessage(e) ?? String(e) });
    }
  };

  // Point the shared completion provider at THIS editor's context. Mode-aware:
  //  - join: NO implicit bare-column table (every ref must be left./right.);
  //    aliases left=from-table, right=to-table.
  //  - validation-query: full statement, no implicit table, no aliases.
  //  - calc field / row filter: implicit from-table (bare columns) + the
  //    always-valid outer./table-name aliases.
  const applyAliases = () => {
    activeSchemaRef.current = schemaInfoRef.current;
    if (mode === "join") {
      activeTablesRef.current = []; // no bare columns in a join expression
      activeSuppressBareRef.current = true; // only left./right., never bare cols
      const toName = schemaInfoRef.current?.relations[1]?.name; // to-table (merged 2nd)
      activeExtraAliasesRef.current = table
        ? {
            left: table.tableName,
            ...(toName ? { right: toName } : {}),
          }
        : {};
    } else if (mode === "validation-query") {
      activeTablesRef.current = []; // full statement — FROM drives scope
      activeSuppressBareRef.current = false;
      activeExtraAliasesRef.current = {};
      activeAtFieldColumnsRef.current = atFieldColumnsRef.current; // @field inputs
    } else {
      activeTablesRef.current = table ? [table.tableName] : [];
      activeSuppressBareRef.current = false;
      activeExtraAliasesRef.current = table
        ? { outer: table.tableName, [table.tableName]: table.tableName }
        : {};
    }
    // @field completion only applies to validation queries.
    if (mode !== "validation-query") activeAtFieldColumnsRef.current = [];
  };

  const onMount = (ed: editor.IStandaloneCodeEditor, monaco: Monaco) => {
    ensureProvider(monaco);
    ed.onDidFocusEditorWidget(() => {
      focusedRef.current = true;
      applyAliases();
    });
    ed.onDidBlurEditorWidget(() => {
      focusedRef.current = false;
      void validate(ed.getValue());
    });
  };

  const border =
    validation.kind === "invalid"
      ? "1px solid #dc2626"
      : validation.kind === "valid"
        ? "1px solid #16a34a"
        : "1px solid #8884";

  return (
    <div className="fw-sql-expression">
      <div style={{ border, borderRadius: 4, overflow: "hidden" }}>
        <Editor
          height={mode === "validation-query" ? "120px" : "80px"}
          defaultLanguage="sql"
          value={value}
          onChange={(v) => props.setValue(v ?? "")}
          onMount={onMount}
          options={{
            minimap: { enabled: false },
            lineNumbers: "off",
            folding: false,
            scrollBeyondLastLine: false,
            wordWrap: "on",
            fontSize: 13,
            suggest: { showWords: false },
            // Auto-pop completion as you type (not only on Ctrl+Space) — the
            // firewall completion catalog is the whole point of this editor, so
            // it should surface without the user knowing the shortcut. `other`
            // covers typing a bare identifier; strings/comments stay quiet.
            quickSuggestions: { other: true, comments: false, strings: false },
            quickSuggestionsDelay: 100,
            // A first character should already offer matches.
            suggestOnTriggerCharacters: true,
            // Render the suggest widget in the document body, NOT inside the
            // editor's container. This editor lives in a form modal with
            // overflow clipping / its own stacking context; without this the
            // widget was computed and populated (provider returns matches) but
            // clipped to nothing — visible in some scroll positions, gone in
            // others. fixedOverflowWidgets lifts it out so it always shows.
            fixedOverflowWidgets: true,
          }}
        />
      </div>
      <div className="dfr-help" style={{ marginTop: 4 }}>
        {validation.kind === "checking" && <span>Validating…</span>}
        {validation.kind === "invalid" && (
          <span style={{ color: "#dc2626" }}>{validation.message}</span>
        )}
        {validation.kind === "unavailable" && <span>{validation.message}</span>}
        {validation.kind === "valid" && (
          <span style={{ color: "#16a34a" }}>
            Valid.
            {validation.transformedSql ? (
              <>
                {" "}
                <span style={{ color: "inherit", opacity: 0.75 }}>
                  firewall runs: <code>{validation.transformedSql}</code>
                </span>
              </>
            ) : null}
          </span>
        )}
        {validation.kind === "idle" && table && (
          <span>
            Completing against <b>{table.tableName}</b> — columns, calculated fields, and
            auto-joins. Validates on blur.
          </span>
        )}
        {!table && <span>No table in context — completion and validation disabled.</span>}
      </div>
    </div>
  );
}

FormWidgetService.get().register({
  name: "sql-expression",
  render: (props) => <SqlExpressionControl {...props} />,
});

export {}; // module side-effect: the register() above
